import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; // 🎯 استدعاء الترجمة
import { 
    Plus, Search, FileText, ChevronLeft, ChevronRight, 
    ChevronsLeft, ChevronsRight, ArrowUpDown, Filter, RefreshCw, Box, X, Tag, List, CreditCard, Zap,  
    Save, ArrowLeft, Paperclip, History, Calendar, ShieldCheck,  
    Layout, Settings, Layers, Trash2, Workflow, MoreVertical, Minus, AlertTriangle, Type
} from 'lucide-react';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';

import { usePermissions } from './usePermissions';
import CustomFieldSlot from './CustomFieldSlot';
import { useFontSize } from './FontSizeContext';
import UniversalLookup from './UniversalLookup';
import TaxCategoryModal from './TaxCategoryModal';
import StatusModal from './StatusModal';
import StatusHistoryModal from './StatusHistoryModal';
import AttachmentsModal from './AttachmentsModal';
import AuditHistoryModal from './AuditHistoryModal';
import CreatePOModal from './CreatePOModal';
import WorkflowActionModal from './WorkflowActionModal';
import WorkflowHistoryModal from './WorkflowHistoryModal';
import SmartPrintButton from './PrintButton'; 

const PODetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  const [fieldMaps, setFieldMaps] = useState({});
  const [activeTab, setActiveTab] = useState('PO');
  const [selectedLineIdx, setSelectedLineIdx] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Modals Control
  const [isTaxModalOpen, setIsTaxModalOpen] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [selectedPRForPO, setSelectedPRForPO] = useState(null);
  const [isWFHistoryOpen, setIsWFHistoryOpen] = useState(false);
  const [showAudit, setShowAudit] = useState(false);

  const [wfContext, setWfContext] = useState({ can_start: false, can_action: false });
  const { canCreate, canUpdate, canDelete } = usePermissions('po');
  const [count, setCount] = useState(0); 
  const location = useLocation();
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;
  const [nav, setNav] = useState({ first: null, last: null, next: null, prev: null, total: 0, current_pos: 0 });

  // 🎯 إعدادات الترجمة والاتجاه
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const sessionSite = session.site_id || '';
  const sessionOrg = session.org_id || '';  

  const [data, setData] = useState({
    ponum: '', description: '', status: 'WAPPR', siteid: sessionSite, orgid: sessionOrg,
    requestedby: session.username || '', 
    totalcost: 0, tax_total: 0, grand_total: 0,
    ref_mrnum: '', ref_prnum: '', requireddate: '', vendor: '',
    orderddate: new Date().toISOString().split('T')[0],
    lines: []
  });

  const [lookup, setLookup] = useState({ isOpen: false, title: '', type: '', field: '', filters: {}, targetIdx: null });
  const isReadOnly = ['APPROVED', 'APPR', 'COMPLETE', 'CAN', 'CLOSED'].includes(data.status);
  const isEditable = !isReadOnly && (isNew ? canCreate : canUpdate);

  const loadPO = useCallback(async () => {
    if (isNew) return;
    try {
      const res = await axios.get(`/api/po/${id}`);
      res.data.lines = Array.isArray(res.data.lines) ? res.data.lines : [];
      setData(res.data);
    } catch (err) { toast.error(t('common.load_fail')); }
  }, [id, isNew, t]);

  useEffect(() => { loadPO(); }, [loadPO]);

  useEffect(() => {
    const openLog = () => setShowAudit(true);
    window.addEventListener('GLOBAL_LOG_HANDLER', openLog);
    return () => window.removeEventListener('GLOBAL_LOG_HANDLER', openLog);
  }, []);

  useEffect(() => {
    if (isNew && data.orgid) {
        setData(prev => ({
          ...prev, description: '', status: 'WAPPR', siteid: session.site_id || '', orgid: session.org_id || '',
          orderddate: new Date().toISOString().split('T')[0], requestedby: session.username || '',
          totalcost: 0, tax_total: 0, grand_total: 0, ref_prnum: '', requireddate: '', vendor: '', lines: []
        }));         
        axios.get(`/api/po/next-number/${data.orgid}`).then(res => {
            setData(prev => ({ ...prev, ponum: res.data.next_ponum }));
        });
    }
  }, [isNew, data.orgid, session.site_id, session.org_id, session.username]);

  const loadNavigation = useCallback(async () => {
    const searchParams = new URLSearchParams(location.search); 
    if (isNew) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`/api/po/${id}/navigation`,{
        params: {
                  all_sites: searchParams.get('all_sites') || "false",
                  siteid: sessionSite, orgid: sessionOrg,
                  filter_site: searchParams.get('siteid') || "", 
                  ponum: searchParams.get('ponum') || "",
                  description: searchParams.get('description') || "",
                  status: searchParams.get('status') || "",
                  sortBy: searchParams.get('sortBy') || "ponum",
                  sortDir: searchParams.get('sortDir') || "desc"
        },
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setNav(res.data || { first: null, last: null, next: null, prev: null, total: 0, current_pos: 0 });
    } catch (err) { setNav({ first: null, last: null, next: null, prev: null, total: 0, current_pos: 0 }); }
  }, [id, isNew, location.search, sessionSite, sessionOrg]);

  useEffect(() => { loadNavigation(); }, [loadNavigation]);

  const fetchWFStatus = async () => {
    if (isNew) return;
    try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`/api/workflow/check-runtime/PO/${id}`, {
            params: { site_id: sessionSite, org_id: sessionOrg },
            headers: { 'Authorization': `Bearer ${token}` }
        });
        setWfContext(res.data);
    } catch (err) { console.error("WF Check Failed", err); }
  };

  useEffect(() => { fetchWFStatus(); }, [id, data.status, sessionSite, sessionOrg]);
  
  const fetchCount = useCallback(async () => {
    if (!data.ponum || isNew) { setCount(0); return; }
    try {
      const res = await axios.get(`/api/attachments/count/PO/${data.ponum}`);
      setCount(res.data.count || 0);
    } catch (err) { console.error("Error fetching attachment count:", err); }
  }, [data.ponum, isNew]);

  const recalculateTotals = (lines) => {
    let totalBase = 0;
    let totalTax = 0;

    const updatedLines = lines.map(l => {
      const qty = parseFloat(l.qty || 0);
      const unitcost = parseFloat(l.unitcost || 0);
      const taxRate = parseFloat(l.taxrate || 0);
      
      const lineBase = qty * unitcost;
      const lineTax = lineBase * (taxRate / 100);
      
      totalBase += lineBase;
      totalTax += lineTax;

      return { ...l, taxamount: lineTax, linecost: lineBase + lineTax };
    });

    setData(prev => ({
      ...prev, lines: updatedLines, totalcost: totalBase, tax_total: totalTax, grand_total: totalBase + totalTax
    }));
  };

  const fetchAndFillMR = async (mrnum) => {
    try {
      const loadId = toast.loading(t('po.messages.fetchingLines', { num: mrnum }));
      const res = await axios.get(`/api/inventory/mr/lines/${mrnum}`);
      const mrLines = res.data;

      if (mrLines && mrLines.length > 0) {
        const newLines = mrLines.map(line => ({
          itemnum: line.itemnum, description: line.description, qty: line.qty_requested || 0,
          unitcost: line.unitcost || 0, tax_id: '', taxrate: 15,
          taxamount: (line.qty_requested * line.unitcost) * 0.15,
          linecost: (line.qty_requested * line.unitcost) * 1.15,
          glaccount: line.glaccount || '', ref_mrnum: mrnum 
        }));

        const combinedLines = [...(data.lines || []), ...newLines];
        recalculateTotals(combinedLines);
        setData(prev => ({ ...prev, ref_mrnum: mrnum }));
        toast.success(t('po.messages.loadedLines', { count: mrLines.length }), { id: loadId });
      } else {
        toast.error(t('po.messages.noLinesFound'), { id: loadId });
      }
    } catch (err) { toast.error(t('po.messages.failedLines')); }
  };

  const fetchAndFillPR = async (prnum) => {
    try {
      const loadId = toast.loading(t('po.messages.fetchingLines', { num: prnum }));
      const res = await axios.get(`/api/inventory/pr/lines/${prnum}`);
      const prLines = res.data;

      if (prLines && prLines.length > 0) {
        const newLines = prLines.map(line => ({
          itemnum: line.itemnum, description: line.description, qty: line.qty || 0,
          unitcost: line.unitcost || 0, tax_id: '', taxrate: 15,
          taxamount: (line.qty * line.unitcost) * 0.15,
          linecost: (line.qty * line.unitcost) * 1.15,
          glaccount: line.glaccount || '', ref_prnum: prnum 
        }));

        const combinedLines = [...(data.lines || []), ...newLines];
        recalculateTotals(combinedLines);
        setData(prev => ({ ...prev, ref_prnum: prnum }));
        toast.success(t('po.messages.loadedLines', { count: prLines.length }), { id: loadId });
      } else {
        toast.error(t('po.messages.noLinesFound'), { id: loadId });
      }
    } catch (err) { toast.error(t('po.messages.failedLines')); }
  };

  const handleSave = async () => {
    if (isReadOnly) return;
    
    // 🎯 حاجز الـ Vendor Mandatory
    if (!data.vendor || data.vendor.trim() === '') {
        toast.error(t('po.messages.vendorRequired'));
        return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token'); 
      if (!token) {
        toast.error(t('common.sessionExpired'));
        navigate('/login');
        return;
      }

      const res = await axios.post('/api/po/save', data, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      toast.success(t('common.save_success'));
      if (isNew) navigate(`/po/${res.data.ponum}`);
    } catch (err) { 
      toast.error(t('common.save_fail') + ": " + (err.response?.data?.detail || "System Error"));
    } finally { setLoading(false); }
  };

  const handleDelete = async () => {
      if (data.status !== 'WAPPR') {
          return Swal.fire({
              title: t('common.locked'), text: t('po.messages.cantDeleteStatus', {status: data.status}),
              icon: 'error', confirmButtonColor: '#1a3353'
          });
      }
  
      const result = await Swal.fire({
          title: t('common.are_you_sure'), text: t('po.swal.deleteWarnText'),
          icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33',
          cancelButtonColor: '#1a3353', confirmButtonText: t('common.yes')
      });
  
      if (result.isConfirmed) {
          try {
              const token = localStorage.getItem('token');
              await axios.delete(`/api/po/${data.ponum}`, {
                  headers: { 'Authorization': `Bearer ${token}` }
              });
              toast.success(t('common.delete_success'));
              navigate('/po'); 
          } catch (err) { toast.error(err.response?.data?.detail || t('common.delete_fail')); }
      }
  };  
  
  const handleRouteWorkflow = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/workflow/initiate', {
        module: 'PO', ref_id: id 
      }, { headers: { 'Authorization': `Bearer ${token}` } });
      toast.success(t('workflow.toasts.initiated'));
      loadPO();
      fetchWFStatus();
    } catch (err) { toast.error(t('workflow.toasts.failed')); }
  };

  const handleSearch = (fieldName, title, defaultType, lineIdx = null) => {
    let extraFilters = { site_id: data.siteid, org_id: data.orgid };
    if (fieldName === 'ref_prnum' || fieldName === 'prnum') extraFilters.status = 'APPR'; 

    setLookup({ isOpen: true, title: title, type: defaultType, field: fieldName, filters: extraFilters, targetIdx: lineIdx });
  };

  const addNewLine = () => {
    const newLines = [...(data.lines || []), { itemnum: '', description: '', qty: 1, unitcost: 0, tax_id: '', taxrate: 0, taxamount: 0, linecost: 0, glaccount: '' }];
    setData({ ...data, lines: newLines });
    setSelectedLineIdx(newLines.length - 1);
  };

  const updateLine = (field, value) => {
    const newLines = [...data.lines];
    newLines[selectedLineIdx][field] = value;
    recalculateTotals(newLines);
  };

  // 🎯 الزرار مكانش موجود أصلاً في جدول أسطر الـ PO - الحماية من التعديل
  // على سجل APPR/CLOSE موجودة فعلاً في isReadOnly، فبنستخدمها هنا بالظبط
  // زي أي حقل تاني في التاب ده
  const removeLine = async (idx) => {
    if (isReadOnly) return;
    const result = await Swal.fire({
      title: t('swal.delete_title'),
      text: t('po.messages.deleteLineConfirm', 'Delete this line?'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: t('common.delete'),
      cancelButtonText: t('common.cancel'),
    });
    if (!result.isConfirmed) return;
    const newLines = data.lines.filter((_, i) => i !== idx);
    recalculateTotals(newLines);
    setSelectedLineIdx(prev => {
      if (prev === null) return null;
      if (prev === idx) return null;
      if (prev > idx) return prev - 1;
      return prev;
    });
  };

  const handleLookupSelect = (row) => {
    const val = row.taxid || row.mrnum || row.prnum || row.value || row.id || row.itemnum || row.comp_num;
    
    if (lookup.field === 'prnum' || lookup.field === 'ref_prnum') {
      fetchAndFillPR(val);
    } else if (lookup.field === 'vendor') {
      setData({ ...data, vendor: val });
    } else if (lookup.field === 'tax_id') {
      const newLines = [...data.lines];
      const idx = lookup.targetIdx;
      newLines[idx].tax_id = row.taxid;
      newLines[idx].taxrate = row.taxrate || 0;
      recalculateTotals(newLines);
    } else if (lookup.field === 'itemnum' && lookup.targetIdx !== null) {
      const newLines = [...data.lines];
      const idx = lookup.targetIdx;
      newLines[idx].itemnum = row.itemnum;
      newLines[idx].description = row.description;
      newLines[idx].itemtype = row.record_type;
      newLines[idx].unitcost = row.avgcost || 0;
      if (row.tax_id) {
          newLines[idx].tax_id = row.tax_id;
          newLines[idx].taxrate = row.taxrate || 0;
      }
      recalculateTotals(newLines);
    } else {
      setData({ ...data, [lookup.field]: val });
    }
    setLookup({ ...lookup, isOpen: false });
  };
  
  useEffect(() => {
    const triggerSave = () => handleSave();
    window.addEventListener('GLOBAL_SAVE_HANDLER', triggerSave);
    return () => window.removeEventListener('GLOBAL_SAVE_HANDLER', triggerSave);
  }, [handleSave]);
  
  useEffect(() => {
    const triggerDelete = () => handleDelete();
    window.addEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
    return () => window.removeEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
  }, [handleDelete]);
  
  useEffect(() => {
    const openStatusModal = () => {
        if (isReadOnly && data.status === 'CAN') return Swal.fire({title: t('common.locked'), text: t('po.messages.cantChangeCancel'), icon: 'lock'});
        
        // 🎯 حاجز الـ Vendor Mandatory
        if (!data.vendor || data.vendor.trim() === '') {
            toast.error(t('po.messages.vendorRequired'));
            return;
        }
        
        setShowStatusModal(true);
    };
    window.addEventListener('GLOBAL_STATUS_HANDLER', openStatusModal);
    return () => window.removeEventListener('GLOBAL_STATUS_HANDLER', openStatusModal);
  }, [isReadOnly, data.status, data.vendor, t]);

  useEffect(() => {
    const handleOpenHistory = () => { setShowHistoryModal(true); };
    window.addEventListener('OPEN_STATUS_HISTORY', handleOpenHistory);
    return () => { window.removeEventListener('OPEN_STATUS_HISTORY', handleOpenHistory); };
  }, []);
  
  useEffect(() => {
    const handleOpenTax = () => { setIsTaxModalOpen(true); };
    window.addEventListener('OPEN_TAX_CATEGORY', handleOpenTax);
    return () => { window.removeEventListener('OPEN_TAX_CATEGORY', handleOpenTax); };
  }, []);
  
  useEffect(() => { fetchCount(); }, [data.ponum, fetchCount]);
  
  const goTo = (target) => target && navigate(`/po/${target}${location.search}`);

  return (
    <div className={`flex flex-col h-full bg-surface-app text-[11px] uppercase tracking-tighter ${isRTL ? 'font-cairo dir-rtl text-right' : 'text-left'}`} style={{ fontSize: `${fontSize}px` }}>
      <UniversalLookup {...lookup} onClose={() => setLookup({ ...lookup, isOpen: false })} onSelect={handleLookupSelect} />
      
      {isWFHistoryOpen && <WorkflowHistoryModal module="PO" refId={id} onClose={() => setIsWFHistoryOpen(false)} />}
      
      {isTaxModalOpen && (
        <TaxCategoryModal  
          isOpen={isTaxModalOpen} onClose={() => setIsTaxModalOpen(false)} orgId={data.orgid}
        />
      )}

      {/* مودال مخصص إذا كان هنالك PR يتم استدعاؤه */}
      <CreatePOModal 
        isOpen={!!selectedPRForPO} prData={selectedPRForPO} onClose={() => setSelectedPRForPO(null)}
        onComplete={(newPoNum) => { setSelectedPRForPO(null); navigate(`/po/${newPoNum}`); }}
      />      
      
      {(!isNew && showStatusModal) && (
        <StatusModal 
          currentStatus={data.status} recordId={data.ponum} appName="PO"
          domainId={data.status === 'APPR' ? 'CANCEL_ONLY' : fieldMaps['status']}
          onClose={() => setShowStatusModal(false)} onComplete={loadPO}
        />)}
        
      {(!isNew && showHistoryModal) && <StatusHistoryModal isOpen={showHistoryModal} appName="PO" recordId={data.ponum} onClose={() => setShowHistoryModal(false)} />}
      
      {!isNew && (
      <AttachmentsModal 
          isOpen={isDocModalOpen} onClose={() => {setIsDocModalOpen(false);fetchCount();}} 
          ownertable="PO" ownerid={data.ponum}
      />)}
      <AuditHistoryModal isOpen={showAudit} onClose={() => setShowAudit(false)} tableName="po" recordId={data.ponum} />

      {/* --- TOP TOOLBAR --- */}
      <div className={`bg-primary-dark text-white p-1.5 flex justify-between items-center shadow-lg z-30 px-4 shrink-0 `}>
        <div className={`flex items-center gap-3 `}>
          <button onClick={() => navigate('/po')} className="p-1.5 hover:bg-white/10 rounded-full transition-colors text-white/70 active:scale-90"><ArrowLeft size={18} className={isRTL ? 'rotate-180' : ''} /></button>
          {!isNew && (
            <div className={`flex items-center bg-black/20 border border-white/10 rounded-md px-1 py-0.5 gap-1 `}>
              <button disabled={!nav.prev} onClick={() => goTo(nav.first)} className="p-1 hover:text-status-warning disabled:opacity-30"><ChevronLeft size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
              <button disabled={!nav.prev} onClick={() => goTo(nav.prev)} className="p-1 hover:text-status-warning disabled:opacity-30"><ChevronLeft size={16} className={isRTL ? 'rotate-180' : ''}/></button>
              <div className="px-2 text-[10px] font-mono border-x border-white/10 mx-1"><span className="text-status-warning">{nav.current_pos}</span> / {nav.total}</div>
              <button disabled={!nav.next} onClick={() => goTo(nav.next)} className="p-1 hover:text-status-warning disabled:opacity-30"><ChevronRight size={16} className={isRTL ? 'rotate-180' : ''}/></button>
              <button disabled={!nav.next} onClick={() => goTo(nav.last)} className="p-1 hover:text-status-warning disabled:opacity-30"><ChevronRight size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
            </div>
          )}
        </div>

        <div className={`flex items-center gap-2 `}>
          {isEditable && (
          <button onClick={handleSave} disabled={loading} className="bg-status-success hover:bg-status-success text-white px-4 py-1.5 rounded-sm flex items-center gap-2 text-[11px] transition-all shadow-md active:scale-95 uppercase">
            <Save size={14} /> {loading ? t('common.saving') : t('common.save')}
          </button>
          )}

          {wfContext.can_start && data.status !== 'APPR' && data.status !== 'CLOSED' && data.status !== 'COMPLETE' && (
            <button onClick={handleRouteWorkflow} className="bg-status-warning hover:bg-status-warning text-white border-0 flex items-center gap-2 text-[11px] font-black h-8 shadow-md px-4 rounded-sm active:scale-95 uppercase">
              <Zap size={14} fill="currentColor" /> {t('po.actions.routeWf')}
            </button>
          )}

          <WorkflowActionModal moduleName="PO" refId={id} currentStatus={data.status} onActionComplete={loadPO} />
          <button onClick={() => setIsWFHistoryOpen(true)} className="p-2 hover:bg-white/10 rounded text-white/70 transition-colors" title={t('po.actions.wfHistory')}><Workflow size={16} className="text-status-warning"/></button>

          <div className="h-6 w-[1px] bg-white/10 mx-1"></div>
          <SmartPrintButton moduleName="PO" refId={`${data.ponum}`} />
          <button onClick={() => setShowStatusModal(true)} disabled={isReadOnly} className="p-2 hover:bg-white/10 rounded text-white/70 transition-colors" title={t('common.change_status')}><Tag size={16}/></button>
          <button onClick={() => setIsDocModalOpen(true)} className="relative p-2 hover:bg-white/10 rounded text-white/70 transition-colors" title={t('common.attachments')}>
            <Paperclip size={16} />
            {count > 0 && <span className="absolute top-1 right-1 bg-status-danger text-[8px] w-3.5 h-3.5 rounded-full flex items-center justify-center font-black">{count}</span>}
          </button>
          <button onClick={() => setShowHistoryModal(true)} className="p-2 hover:bg-white/10 rounded text-white/70 transition-colors" title={t('common.history')}><History size={16}/></button>
          {!isNew && canDelete && (
            <button onClick={handleDelete} className="p-2 text-status-danger hover:bg-status-danger/20 rounded transition-colors" title={t('common.delete')}>
              <Trash2 size={16}/>
            </button>
          )}

          <div className={`flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full border border-white/20 shadow-inner ${isRTL ? 'mr-4 flex-row-reverse' : 'ml-4'}`}>
            <button onClick={() => changeFontSize(-1)} className="hover:text-status-warning transition-colors"><Minus size={14} /></button>
            <div className="flex items-center gap-1 min-w-[40px] justify-center text-white/70">
              <Type size={14} /><span className="font-black text-[10px]">{fontSize}</span>
            </div>
            <button onClick={() => changeFontSize(1)} className="hover:text-status-warning transition-colors"><Plus size={14} /></button>
          </div>
        </div>
      </div>

      {/* --- DOCUMENT HEADER --- */}
      <div className={`bg-surface-card border-b border-line px-6 py-3 flex items-center gap-12 shadow-sm z-20 shrink-0 `}>
        <HeaderField label={t('po.fields.poNum')} value={data.ponum} isBold width="w-32" disabled isRTL={isRTL} />
        <HeaderField label={t('assets.headers.desc')} value={data.description} onChange={v => setData({...data, description: v})} width="w-[400px]" disabled={isReadOnly} placeholder={t('po.placeholders.desc')} isRTL={isRTL} />
        <HeaderField label={t('assets.headers.status')} value={data.status} disabled width="w-24" isRTL={isRTL} />
        <HeaderField label={t('assets.headers.site')} value={data.siteid} disabled width="w-24" isRTL={isRTL} />
      </div>

      {/* --- TABS --- */}
      <div className={`flex bg-surface-card border-b border-line-subtle px-4 shrink-0 `}>
        {['PO', 'PO Lines'].map(tKey => (
          <button key={tKey} onClick={() => setActiveTab(tKey)}
            className={`px-8 py-3 flex items-center gap-2 transition-all text-[10px] font-black uppercase tracking-tighter
            ${activeTab === tKey ? 'border-b-2 border-primary-dark text-primary-dark bg-surface-panel' : 'text-ink-secondary hover:text-ink'} `}>
            {tKey === 'PO' ? <FileText size={13}/> : <List size={13}/>} {t(`po.tabs.${tKey.replace(' ', '').toLowerCase()}`)}
          </button>
        ))}
      </div>

      {/* --- CONTENT AREA --- */}
      <div className="flex-1 overflow-y-auto p-4 custom-scrollbar bg-surface-panel">
        {activeTab === 'PO' && (
          <div className="grid grid-cols-12 gap-6 max-w-7xl mx-auto animate-in fade-in duration-500 pb-20">
            <div className="col-span-8 space-y-6">
              <Section title={t('po.sections.general')} isRTL={isRTL}>
                <div className={`grid grid-cols-2 gap-x-12 gap-y-4 ${isRTL ? 'dir-rtl' : ''}`}>
                  <EditableField label={t('po.fields.requestedBy')} value={data.requestedby} disabled={isReadOnly} onChange={v => setData({...data, requestedby: v})} isRTL={isRTL} />
                  <DateField label={t('po.fields.orderDate')} value={data.orderddate} onChange={v => setData({...data, orderddate: v})} isRTL={isRTL} />
                  <EditableField label={t('po.fields.refPr')} value={data.prnum} onSearch={() => handleSearch('prnum', t('common.search'), 'purchaserequest')} disabled={isReadOnly} isRTL={isRTL} />
                  <EditableField label={t('po.fields.vendor')} value={data.vendor} disabled={isReadOnly} onChange={v => setData({...data, vendor: v})} onSearch={() => handleSearch('vendor', t('common.search'), 'vendors')} isRTL={isRTL} />
                  <StaticField label={t('assets.headers.site')} value={data.siteid} isRTL={isRTL} />
                  <StaticField label={t('assets.list.col_org')} value={data.orgid} isRTL={isRTL} />
                  <CustomFieldSlot coreModelName="PO" tab="PO" section="General" recordId={data.ponum} disabled={isNew} />
                </div>
              </Section>
            </div>

            <div className="col-span-4 space-y-6">
              <Section title={t('po.sections.financial')} isRTL={isRTL}>
                <div className={`space-y-4 font-mono ${isRTL ? 'text-right' : 'text-left'}`}>
                  <div className={`flex justify-between border-b border-line pb-1 `}>
                    <span className="text-[9px] text-ink-secondary font-black uppercase italic">{t('po.financial.subtotal')}</span>
                    <span className="font-bold text-ink">{data.totalcost?.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                  </div>
                  <div className={`flex justify-between border-b border-line pb-1 text-status-danger `}>
                    <span className="text-[9px] font-black uppercase tracking-widest">{t('po.financial.taxTotal')}</span>
                    <span className="font-black">+{data.tax_total?.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                  </div>
                  <div className={`flex justify-between bg-primary-soft p-3 rounded-md border border-primary-soft shadow-inner mt-4 `}>
                    <span className={`font-black text-primary uppercase text-[10px] flex items-center gap-1 `}><CreditCard size={12}/> {t('po.financial.grandTotal')}</span>
                    <span className="text-xl font-black text-primary tracking-tighter">{data.grand_total?.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                  </div>
                </div>
              </Section>
            </div>
          </div>
        )}

        {activeTab === 'PO Lines' && (
          <div className="max-w-7xl mx-auto space-y-4 animate-in slide-in-from-bottom-4 duration-500 mb-10">
            {/* 1. الجدول العلوي (The Master Table) */}
            <div className="bg-surface-card border rounded shadow-sm overflow-hidden">
              <div className={`p-1 bg-surface-panel font-bold border-b text-[0.8] flex justify-between items-center px-3 text-ink-secondary ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <span>{t('po.sections.lines')}</span>
                  <Filter size={12}/>
                  {isRTL ? <ChevronLeft size={12}/> : <ChevronRight size={12}/>}
                  <span className="text-ink-secondary font-normal">
                    {data.lines?.length > 0 ? `${selectedLineIdx + 1} - ${data.lines.length}` : '0'} {t('common.of')} {data.lines?.length || 0}
                  </span>
                </div>
                <button onClick={addNewLine} disabled={isReadOnly} className="bg-primary text-white px-3 py-1 rounded-sm flex items-center gap-1.5 text-[0.85em] font-black hover:bg-black uppercase shadow-sm transition-all active:scale-95 disabled:opacity-40">
                  <Plus size={13}/> {t('po.actions.addManualRow')}
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className={`w-full text-left ${isRTL ? 'text-right' : 'text-left'}`}>
                  <thead className="bg-surface-panel border-b text-ink-secondary font-bold text-[0.8]">
                    <tr>
                      <th className="p-2 w-8"></th>
                      <th className={`p-2 border-r text-primary underline ${isRTL ? 'text-right' : ''}`}>{t('po.tables.itemNum')}</th>
                      <th className={`p-2 border-r ${isRTL ? 'text-right' : ''}`}>{t('assets.headers.desc')}</th>
                      <th className={`p-2 border-r ${isRTL ? 'text-right' : ''}`}>{t('po.tables.itemType')}</th>
                      <th className="p-2 border-r text-right">{t('wo.tables.qty')}</th>
                      <th className="p-2 border-r text-right">{t('po.tables.unitCost')}</th>
                      <th className="p-2 border-r text-center text-status-danger">{t('po.tables.tax')} %</th>
                      <th className="p-2 border-r text-right bg-primary-soft/30">{t('po.tables.lineTotal')}</th>
                      <th className="p-2 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(Array.isArray(data.lines) ? data.lines : []).map((l, i) => (
                      <tr key={i} onClick={() => setSelectedLineIdx(i)}
                        className={`border-b hover:bg-primary-soft cursor-pointer text-[12px] ${selectedLineIdx === i ? 'bg-primary-soft' : ''}`}>
                        <td className="p-2 text-center text-primary">
                          {selectedLineIdx === i && (isRTL ? <ChevronLeft size={12}/> : <ChevronRight size={12}/>)}
                        </td>
                        <td className={`p-2 border-r font-bold text-primary ${isRTL ? 'text-right' : ''}`}>{l.itemnum || '---'}</td>
                        <td className={`p-2 border-r ${isRTL ? 'text-right' : ''}`}>{l.description}</td>
                        <td className={`p-2 border-r text-ink-secondary italic ${isRTL ? 'text-right' : ''}`}>{l.itemtype}</td>
                        <td className="p-2 border-r text-right font-mono">{l.qty}</td>
                        <td className="p-2 border-r text-right font-mono">{l.unitcost?.toLocaleString()}</td>
                        <td className="p-2 border-r text-center font-bold text-status-danger">{l.taxrate || 0}%</td>
                        <td className="p-2 border-r text-right font-black text-status-success bg-primary-soft/10 font-mono">{parseFloat(l.linecost || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                        <td className="p-2 text-center">
                          {!isReadOnly && (
                            <Trash2 size={14} className="text-ink-secondary hover:text-status-danger mx-auto" onClick={(e) => { e.stopPropagation(); removeLine(i); }} />
                          )}
                        </td>
                      </tr>
                    ))}
                    {(!data.lines || data.lines.length === 0) && (
                       <tr><td colSpan="9" className="p-20 text-center text-ink-secondary italic uppercase font-black tracking-widest">{t('po.messages.noLines')}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 2. قسم التفاصيل (The Detail Section) */}
            {selectedLineIdx !== null && data.lines && data.lines[selectedLineIdx] && (
              <div className="animate-in fade-in duration-300">
                <div className={`bg-surface-panel p-2 font-bold text-ink border border-b-0 text-[0.8] ${isRTL ? 'text-right' : ''}`}>
                  {t('po.sections.lineDetails', 'Line Details')}
                </div>
                <div className={`bg-surface-card border p-4 grid grid-cols-5 gap-x-6 gap-y-4 shadow-sm ${isRTL ? 'text-right' : ''}`}>
                  <EditableField label={t('po.tables.itemNum')} value={data.lines[selectedLineIdx].itemnum} onSearch={() => handleSearch('itemnum', t('common.search'), 'itemstools', selectedLineIdx)} disabled={isReadOnly} isRTL={isRTL} />
                  <EditableField label={t('wo.tables.qty')} type="number" value={data.lines[selectedLineIdx].qty} disabled={isReadOnly} onChange={v => updateLine('qty', v)} isRTL={isRTL} />
                  <EditableField label={t('po.tables.unitCost')} type="number" value={data.lines[selectedLineIdx].unitcost} disabled={isReadOnly} onChange={v => updateLine('unitcost', v)} isRTL={isRTL} />
                  <EditableField label={`${t('po.tables.taxCat')} (${data.lines[selectedLineIdx].taxrate || 0}%)`} value={data.lines[selectedLineIdx].tax_id} onSearch={() => handleSearch('tax_id', t('common.search'), 'tax_category', selectedLineIdx)} disabled={isReadOnly} isRTL={isRTL} />
                  <EditableField label={t('po.fields.glAccount')} value={data.lines[selectedLineIdx].glaccount} disabled={isReadOnly} onChange={v => updateLine('glaccount', v)} isRTL={isRTL} />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// --- المكونات الداخلية الصافية ---
const HeaderField = ({ label, value, onChange, width, isBold, disabled, placeholder, isRTL }) => (
  <div className={`flex flex-col px-4 border-line shrink-0 ${isRTL ? 'border-l text-right last:border-0' : 'border-r text-left last:border-0'}`}>
    <label className="text-[0.8em] text-ink-secondary font-black uppercase tracking-widest mb-0.5 italic leading-none">{label}</label>
    <input className={`${width} border-b border-transparent outline-none py-0.5 font-black uppercase ${isBold ? 'text-primary-dark text-[13px] tracking-tighter' : 'text-primary text-[12px]'} bg-transparent transition-all focus:border-primary placeholder:opacity-30 placeholder:lowercase placeholder:font-normal placeholder:italic ${isRTL ? 'text-right' : 'text-left'}`}
           value={value || ''} disabled={disabled} onChange={e => onChange && onChange(e.target.value)} placeholder={placeholder} />
  </div>
);

const Section = ({ title, children, isRTL }) => (
  <div className="bg-surface-card border rounded shadow-sm transition-all hover:shadow-md border-line overflow-hidden">
    <div className={`p-2.5 bg-surface-panel border-b border-line-subtle font-black text-ink-secondary uppercase text-[0.9em] tracking-widest flex items-center gap-2 italic `}>
      <div className="w-1.5 h-4 bg-status-warning rounded-full shadow-sm"></div> {title}
    </div>
    <div className="p-6">{children}</div>
  </div>
);

const EditableField = ({ label, value, onChange, onSearch, type = "text", disabled, isRTL }) => (
  <div className={`flex flex-col gap-1 border-b border-line py-1.5 px-1 group focus-within:border-primary transition-all ${isRTL ? 'text-right' : 'text-left'}`}>
    <label className="text-[0.8em] font-black text-ink-secondary uppercase tracking-widest italic leading-none">{label}</label>
    <div className={`flex justify-between items-center h-6 `}>
      <input type={type} className={`outline-none font-black text-primary-dark bg-transparent w-full text-[12px] tracking-tighter disabled:opacity-50 ${isRTL ? 'text-right' : 'text-left'}`}
        value={value || ''} disabled={disabled} onChange={e => onChange && onChange(e.target.value)} />
      {onSearch && !disabled && <Search size={14} className="text-primary cursor-pointer hover:text-primary-dark transition-all hover:scale-110 active:scale-90 shrink-0" onClick={onSearch} />}
    </div>
  </div>
);

const StaticField = ({ label, value, isRTL }) => (
  <div className={`flex flex-col border-b border-line-subtle py-1.5 px-1 bg-surface-card/40 shadow-inner rounded-sm mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
    <label className="text-[0.8em] font-black text-ink-secondary uppercase tracking-widest italic">{label}</label>
    <div className="font-black uppercase tracking-tighter text-primary-dark text-[12px] mt-0.5 italic">{value || '---'}</div>
  </div>
);

const DateField = ({ label, value, onChange, isRTL }) => (
  <div className={`flex flex-col border-b border-line py-1.5 px-1 group focus-within:border-primary transition-all ${isRTL ? 'text-right' : 'text-left'}`}>
    <label className="text-[0.8em] font-black text-ink-secondary uppercase tracking-widest italic">{label}</label>
    <input type="date" className={`outline-none font-black text-primary-dark bg-transparent py-0.5 text-[12px] ${isRTL ? 'text-right' : 'text-left'}`}
      value={value ? value.split('T')[0] : ''} onChange={e => onChange(e.target.value)} />
  </div>
);

export default PODetails;